import React, { useEffect, useState, useCallback } from "react";
import { Plus, X, Trash2, Shield, Building2, Users as UsersIcon, Boxes, Globe } from "lucide-react";
import { toast } from "sonner";
import { http, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, SectionTitle, Stat, Spinner } from "@/components/Kit";

const ROLE_LABEL = { super_admin: "Super Admin", org_admin: "Org Admin", analyst: "Analyst", client_viewer: "Client (Viewer)" };
const inp = "w-full mt-1 bg-white border border-[#E7D9C6] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#A5300F]";

export default function AdminPanel() {
  const { user } = useAuth();
  const sup = user?.role === "super_admin";
  const [tab, setTab] = useState("Overview");
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState(null);
  const TABS = ["Overview", ...(sup ? ["Organisations"] : []), "Users", "Invites", "Audit Log"];

  useEffect(() => {
    http.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    http.get("/admin/roles").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-6 max-w-[1500px] mx-auto fade-up">
      <div className="flex items-center gap-2 mb-1">
        <Shield className="w-6 h-6 text-[#A5300F]" />
        <h1 className="font-display text-3xl font-bold text-[#A5300F]">Administration</h1>
      </div>
      <p className="text-sm text-[#8A8175] mb-5">Manage organisations, users, roles and module-level access.</p>

      <div className="flex gap-1 border-b border-[#ECE6DD] mb-5">
        {TABS.map((x) => (
          <button key={x} onClick={() => setTab(x)} data-testid={`admin-tab-${x.toLowerCase()}`}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === x ? "border-[#A5300F] text-[#A5300F]" : "border-transparent text-[#8A8175] hover:text-[#A5300F]"}`}>{x}</button>
        ))}
      </div>

      {tab === "Overview" && stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <Stat label="Organisations" value={stats.organisations} />
            <Stat label="Users" value={stats.users} />
            <Stat label="Applications" value={stats.applications} />
            <Stat label="Web Targets" value={stats.web_targets} />
            <Stat label="AppSec Scans" value={stats.appsec_scans} />
            <Stat label="Web Scans" value={stats.web_scans} />
          </div>
          {sup && <ArchivePolicyCard />}
          {sup && <LookalikeScheduleCard />}
        </>
      )}
      {tab === "Organisations" && sup && <OrgsTab />}
      {tab === "Users" && meta && <UsersTab sup={sup} meta={meta} />}
      {tab === "Invites" && <InvitesTab sup={sup} />}
      {tab === "Audit Log" && <AuditTab />}
    </div>
  );
}

function ArchivePolicyCard() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { http.get("/admin/archive-policy").then((r) => setP(r.data)).catch(() => setP(null)); }, []);
  if (!p) return null;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await http.put("/admin/archive-policy", {
        enabled: p.enabled, retention_days: Number(p.retention_days) || 30, heads_up_days: Number(p.heads_up_days) || 0 });
      setP(data); toast.success("Archive policy saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="mt-5 p-5" data-testid="archive-policy-card">
      <SectionTitle>Archive auto-expiry</SectionTitle>
      <p className="text-sm text-[#8A8175] mt-1 mb-4">Automatically delete domains left archived beyond a retention window. Domains nearing deletion get a heads-up countdown in the archived list before they are removed.</p>
      <label className="flex items-center gap-2 text-sm mb-4"><input type="checkbox" checked={p.enabled} onChange={(e) => setP({ ...p, enabled: e.target.checked })} className="accent-[#A5300F]" data-testid="policy-enabled" /> Enable auto-deletion of archived domains</label>
      <div className="grid grid-cols-2 gap-4 max-w-md">
        <div><label className={lbl}>Retention (days)</label><input type="number" min="1" value={p.retention_days} onChange={(e) => setP({ ...p, retention_days: e.target.value })} disabled={!p.enabled} data-testid="policy-retention" className={inp + (p.enabled ? "" : " opacity-50")} /></div>
        <div><label className={lbl}>Heads-up before (days)</label><input type="number" min="0" value={p.heads_up_days} onChange={(e) => setP({ ...p, heads_up_days: e.target.value })} disabled={!p.enabled} data-testid="policy-headsup" className={inp + (p.enabled ? "" : " opacity-50")} /></div>
      </div>
      <button onClick={save} disabled={busy} data-testid="save-policy-btn" className="mt-4 px-4 py-2 text-sm bg-[#A5300F] text-white font-semibold rounded-md hover:bg-[#8F2A0D] disabled:opacity-50">Save policy</button>
    </Card>
  );
}

const DOW = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function LookalikeScheduleCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const load = useCallback(() => { http.get("/admin/lookalike-schedule").then((r) => setS(r.data)).catch(() => setS(null)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!s) return null;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await http.put("/admin/lookalike-schedule", {
        enabled: s.enabled, frequency: s.frequency, day_of_week: Number(s.day_of_week) || 0, hour: Number(s.hour) || 0 });
      setS(data); toast.success("Lookalike schedule saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await http.post("/admin/lookalike-schedule/run-now");
      if (data.queued) { toast.success("Deep lookalike sweep started — runs in the background."); setTimeout(load, 8000); }
      else { toast.info("A sweep is already running."); }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };
  const fmt = (v) => (v ? new Date(v).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");
  return (
    <Card className="mt-5 p-5" data-testid="lookalike-schedule-card">
      <SectionTitle>Scheduled deep lookalike sweep</SectionTitle>
      <p className="text-sm text-[#8A8175] mt-1 mb-4">Automatically re-run the richer dnstwist scan across all active domains on a schedule, so newly-registered impersonation domains surface on the Impersonation board and Domain Security without a manual trigger.</p>
      <label className="flex items-center gap-2 text-sm mb-4"><input type="checkbox" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} className="accent-[#A5300F]" data-testid="lookalike-enabled" /> Enable automatic scheduled sweeps</label>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-2xl">
        <div>
          <label className={lbl}>Frequency</label>
          <select value={s.frequency} onChange={(e) => setS({ ...s, frequency: e.target.value })} disabled={!s.enabled} data-testid="lookalike-frequency" className={inp + (s.enabled ? "" : " opacity-50")}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly (1st)</option>
          </select>
        </div>
        <div>
          <label className={lbl}>Day of week</label>
          <select value={s.day_of_week} onChange={(e) => setS({ ...s, day_of_week: Number(e.target.value) })} disabled={!s.enabled || s.frequency !== "weekly"} data-testid="lookalike-dow" className={inp + (s.enabled && s.frequency === "weekly" ? "" : " opacity-50")}>
            {DOW.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className={lbl}>Hour (UTC)</label>
          <select value={s.hour} onChange={(e) => setS({ ...s, hour: Number(e.target.value) })} disabled={!s.enabled} data-testid="lookalike-hour" className={inp + (s.enabled ? "" : " opacity-50")}>
            {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button onClick={save} disabled={busy} data-testid="save-lookalike-schedule-btn" className="px-4 py-2 text-sm bg-[#A5300F] text-white font-semibold rounded-md hover:bg-[#8F2A0D] disabled:opacity-50">Save schedule</button>
        <button onClick={runNow} disabled={running || s.running} data-testid="run-lookalike-now-btn" className="px-4 py-2 text-sm border border-[#E7C9B5] text-[#A5300F] font-semibold rounded-md hover:bg-[#FFF5EB] disabled:opacity-50">{s.running ? "Sweep running…" : (running ? "Starting…" : "Run sweep now")}</button>
      </div>
      <div className="mt-4 text-[13px] text-[#8A8175] space-y-1" data-testid="lookalike-schedule-status">
        <div>Next scheduled run: <span className="font-semibold text-[#5C554C]">{s.enabled ? fmt(s.next_due_at) : "disabled"}</span></div>
        <div>Last run finished: <span className="font-semibold text-[#5C554C]">{fmt(s.last_finished_at)}</span>{s.last_summary && <span> · {s.last_summary.domains} domains, {s.last_summary.registered} lookalikes</span>}</div>
      </div>
    </Card>
  );
}

function OrgsTab() {
  const [orgs, setOrgs] = useState([]);
  const [name, setName] = useState("");
  const load = () => http.get("/admin/orgs").then((r) => setOrgs(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!name.trim()) return;
    try { await http.post("/admin/orgs", { name }); setName(""); toast.success("Organisation created"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Delete "${o.name}" and ALL its apps, targets, scans, findings and users? This cannot be undone.`)) return;
    try { await http.delete(`/admin/orgs/${o.id}`); toast.success("Organisation deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 p-3 border-b border-[#ECE6DD]">
        <input placeholder="New organisation name" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-org-name"
          className="flex-1 max-w-xs bg-white border border-[#E7D9C6] rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-[#A5300F]" />
        <button onClick={create} data-testid="create-org-btn" className="flex items-center gap-1 bg-[#A5300F] text-white font-semibold text-sm px-3 py-1.5 rounded-md hover:bg-[#8F2A0D]"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <table className="w-full text-sm" data-testid="orgs-admin-table">
        <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left bg-[#FFF5EB] border-b border-[#ECE6DD]">
          <th className="py-2 px-4">Organisation</th><th className="text-center">Users</th><th className="text-center">Apps</th><th className="text-center">Targets</th><th></th>
        </tr></thead>
        <tbody>
          {orgs.map((o) => (
            <tr key={o.id} className="border-b border-[#F4EFE8]" data-testid={`org-row-${o.id}`}>
              <td className="py-2.5 px-4 font-semibold flex items-center gap-2"><Building2 className="w-4 h-4 text-[#B5AC9E]" />{o.name}</td>
              <td className="text-center">{o.user_count}</td><td className="text-center">{o.app_count}</td><td className="text-center">{o.target_count}</td>
              <td className="text-right pr-4"><button onClick={() => remove(o)} data-testid={`delete-org-${o.id}`} className="text-[#A5300F] hover:opacity-70"><Trash2 className="w-4 h-4" /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function UsersTab({ sup, meta }) {
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [edit, setEdit] = useState(null); // user object or {} for new
  const load = () => http.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); if (sup) http.get("/admin/orgs").then((r) => setOrgs(r.data)); }, [sup]);

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try { await http.delete(`/admin/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const resetLink = async (u) => {
    try {
      const { data } = await http.post(`/admin/users/${u.id}/reset-link`);
      const url = window.location.origin + data.reset_path;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success(data.emailed ? `Reset link emailed to ${data.email}` : "Reset link copied to clipboard", { description: url });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <div className="flex justify-end mb-3">
        <button onClick={() => setEdit({})} data-testid="new-user-btn" className="flex items-center gap-2 bg-[#A5300F] text-white font-semibold text-sm px-4 py-2 rounded-md hover:bg-[#8F2A0D]"><Plus className="w-4 h-4" /> New User</button>
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left bg-[#FFF5EB] border-b border-[#ECE6DD]">
            <th className="py-2 px-4">User</th><th>Role</th><th>Organisation</th><th className="text-center">Active</th><th></th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-[#F4EFE8] hover:bg-[#FBF7F1]" data-testid={`user-row-${u.id}`}>
                <td className="py-2.5 px-4"><div className="font-semibold">{u.name}</div><div className="text-xs text-[#8A8175]">{u.email}</div></td>
                <td><span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ color: "#A5300F", background: "#A5300F14" }}>{ROLE_LABEL[u.role] || u.role}</span></td>
                <td className="text-xs text-[#8A8175]">{
                  u.role === "super_admin" ? "— (all)" : (() => {
                    const ids = u.org_ids?.length ? u.org_ids : (u.org_id ? [u.org_id] : []);
                    if (!ids.length) return "—";
                    const names = ids.map((id) => orgs.find((o) => o.id === id)?.name || id);
                    const scoped = Object.keys(u.domain_scope || {}).length;
                    const base = names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0];
                    return scoped ? `${base} · scoped` : base;
                  })()
                }</td>
                <td className="text-center">{u.active ? <span className="text-[#2E7D32] text-xs font-semibold">Yes</span> : <span className="text-[#A5300F] text-xs font-semibold">No</span>}</td>
                <td className="text-right pr-4 whitespace-nowrap">
                  <button onClick={() => setEdit(u)} data-testid={`edit-user-${u.id}`} className="text-[#A5300F] font-semibold text-xs underline mr-3">Edit</button>
                  <button onClick={() => resetLink(u)} data-testid={`reset-link-${u.id}`} className="text-[#8A8175] hover:text-[#A5300F] font-semibold text-xs underline mr-3">Reset link</button>
                  <button onClick={() => remove(u)} data-testid={`delete-user-${u.id}`} className="text-[#A5300F] hover:opacity-70 align-middle"><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {edit && <UserModal sup={sup} meta={meta} orgs={orgs} user={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </>
  );
}

function UserModal({ sup, meta, orgs, user, onClose, onSaved }) {
  const isNew = !user.id;
  const [f, setF] = useState({
    email: user.email || "", password: "", name: user.name || "",
    role: user.role || "client_viewer", active: user.active !== false,
  });
  const initialOrgIds = user.org_ids && user.org_ids.length ? user.org_ids : (user.org_id ? [user.org_id] : (orgs[0] ? [orgs[0].id] : []));
  const [orgIds, setOrgIds] = useState(initialOrgIds);
  const [domainScope, setDomainScope] = useState(user.domain_scope || {});
  const [domainsByOrg, setDomainsByOrg] = useState({});
  const [perms, setPerms] = useState(user.permissions || {});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const roleDefaults = meta.defaults[f.role] || {};
  const effLevel = (cap) => perms[cap] || roleDefaults[cap] || "none";

  const loadDomains = useCallback((oid) => {
    setDomainsByOrg((prev) => {
      if (prev[oid]) return prev;
      http.get("/attack/domains", { params: { org_id: oid } })
        .then((r) => setDomainsByOrg((p) => ({ ...p, [oid]: r.data.map((d) => d.registrable_domain) })))
        .catch(() => setDomainsByOrg((p) => ({ ...p, [oid]: [] })));
      return prev;
    });
  }, []);
  useEffect(() => { orgIds.forEach(loadDomains); }, [orgIds, loadDomains]);

  const toggleOrg = (oid) => {
    setOrgIds((p) => p.includes(oid) ? p.filter((x) => x !== oid) : [...p, oid]);
    setDomainScope((p) => { const n = { ...p }; delete n[oid]; return n; });
    loadDomains(oid);
  };
  const toggleDomain = (oid, dom) => setDomainScope((p) => {
    const cur = new Set(p[oid] || []);
    if (cur.has(dom)) cur.delete(dom); else cur.add(dom);
    const arr = [...cur];
    const next = { ...p };
    if (arr.length) next[oid] = arr; else delete next[oid];
    return next;
  });

  const save = async () => {
    setBusy(true);
    try {
      const body = { name: f.name, role: f.role, permissions: perms, active: f.active };
      if (sup) {
        if (f.role === "super_admin") { body.org_ids = []; body.domain_scope = {}; }
        else { body.org_ids = orgIds; body.domain_scope = domainScope; }
      }
      if (isNew) {
        body.email = f.email; body.password = f.password;
        if (!sup) { delete body.org_ids; delete body.domain_scope; }
        await http.post("/admin/users", body);
      } else {
        if (f.password) body.password = f.password;
        await http.patch(`/admin/users/${user.id}`, body);
      }
      toast.success(isNew ? "User created" : "User updated"); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const families = [...new Set(meta.capabilities.map((c) => c.family))];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <Card className="w-full max-w-2xl my-8 max-h-[calc(100vh-4rem)] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#ECE6DD] sticky top-0 bg-white">
          <SectionTitle>{isNew ? "New User" : `Edit ${user.email}`}</SectionTitle>
          <button onClick={onClose}><X className="w-4 h-4 text-[#8A8175]" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Email</label><input data-testid="user-email" disabled={!isNew} value={f.email} onChange={(e) => set("email", e.target.value)} className={inp + (isNew ? "" : " opacity-60")} /></div>
            <div><label className={lbl}>Name</label><input data-testid="user-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inp} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>{isNew ? "Password" : "New password (optional)"}</label><input data-testid="user-password" type="password" value={f.password} onChange={(e) => set("password", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Role</label>
              <select data-testid="user-role" value={f.role} onChange={(e) => set("role", e.target.value)} className={inp}>
                {meta.roles.filter((r) => sup || r !== "super_admin").map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
              </select>
            </div>
          </div>
          {sup && f.role !== "super_admin" && (
            <div>
              <label className={lbl}>Organisations & domain access</label>
              <div className="mt-2 space-y-2" data-testid="user-org-scope">
                {orgs.map((o) => {
                  const checked = orgIds.includes(o.id);
                  const doms = domainsByOrg[o.id] || [];
                  const scoped = domainScope[o.id] || [];
                  return (
                    <div key={o.id} className="border border-[#ECE6DD] rounded-md">
                      <label className="flex items-center gap-2 px-3 py-2 cursor-pointer">
                        <input type="checkbox" checked={checked} onChange={() => toggleOrg(o.id)}
                          className="accent-[#A5300F]" data-testid={`user-org-check-${o.id}`} />
                        <Building2 className="w-4 h-4 text-[#B5AC9E]" />
                        <span className="text-sm font-semibold">{o.name}</span>
                        {checked && <span className="ml-auto text-[10px] text-[#8A8175]">{scoped.length ? `${scoped.length} of ${doms.length || "?"} domains` : "All domains"}</span>}
                      </label>
                      {checked && doms.length > 0 && (
                        <div className="px-3 pb-2 pl-9 flex flex-wrap gap-1.5">
                          {doms.map((d) => {
                            const on = scoped.includes(d);
                            return (
                              <button key={d} type="button" onClick={() => toggleDomain(o.id, d)}
                                data-testid={`user-domain-${o.id}-${d}`}
                                className={`text-[11px] px-2 py-1 rounded border ${on ? "border-[#A5300F] bg-[#FFF5EB] text-[#A5300F] font-semibold" : "border-[#E7D9C6] text-[#8A8175]"}`}>{d}</button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="text-[10px] text-[#B5AC9E] mt-1">Tick the organisations this user can access. Within an org, optionally pick specific domains — leave all unticked to grant every domain in that org.</div>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#A5300F]" data-testid="user-active" /> Active</label>

          <div>
            <div className="flex items-center justify-between">
              <label className={lbl}>Module access (overrides role defaults)</label>
              <button onClick={() => setPerms({})} className="text-xs text-[#A5300F] underline">Reset to role defaults</button>
            </div>
            <div className="mt-2 space-y-3">
              {families.map((fam) => (
                <div key={fam} className="border border-[#ECE6DD] rounded-md p-3">
                  <div className="text-xs font-bold text-[#5C554C] mb-2">{fam}</div>
                  <div className="space-y-1.5">
                    {meta.capabilities.filter((c) => c.family === fam).map((c) => (
                      <div key={c.key} className="flex items-center justify-between gap-3">
                        <span className="text-sm">{c.label} <span className="text-[10px] text-[#B5AC9E] font-mono">{c.key}</span></span>
                        <div className="flex gap-1" data-testid={`perm-${c.key}`}>
                          {["none", "view", "manage"].map((lv) => {
                            const active = effLevel(c.key) === lv;
                            const isDefault = !perms[c.key] && (roleDefaults[c.key] || "none") === lv;
                            return (
                              <button key={lv} disabled={f.role === "super_admin"}
                                onClick={() => setPerms((p) => ({ ...p, [c.key]: lv }))}
                                data-testid={`perm-${c.key}-${lv}`}
                                className={`text-[11px] px-2 py-1 rounded border ${active ? "border-[#A5300F] bg-[#FFF5EB] text-[#A5300F] font-semibold" : "border-[#E7D9C6] text-[#8A8175]"} ${f.role === "super_admin" ? "opacity-50" : ""}`}>
                                {lv}{isDefault ? "•" : ""}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {f.role === "super_admin" && <div className="text-xs text-[#8A8175]">Super Admin has full access to everything by design.</div>}
              <div className="text-[10px] text-[#B5AC9E]">• marks the level inherited from the role default. Click a level to override for this user.</div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#ECE6DD] sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-3 py-2 text-sm border border-[#E7D9C6] rounded-md">Cancel</button>
          <button onClick={save} disabled={busy || (isNew && (!f.email || !f.password))} data-testid="save-user-btn"
            className="px-4 py-2 text-sm bg-[#A5300F] text-white font-semibold rounded-md hover:bg-[#8F2A0D] disabled:opacity-50 flex items-center gap-2">
            {busy && <Spinner />} {isNew ? "Create user" : "Save changes"}
          </button>
        </div>
      </Card>
    </div>
  );
}

const lbl = "text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold";

function InvitesTab({ sup }) {
  const [invites, setInvites] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [f, setF] = useState({ email: "", role: "client_viewer", org_id: "" });
  const [busy, setBusy] = useState(false);
  const load = () => http.get("/admin/invites").then((r) => setInvites(r.data));
  useEffect(() => { load(); if (sup) http.get("/admin/orgs").then((r) => { setOrgs(r.data); setF((p) => ({ ...p, org_id: r.data[0]?.id || "" })); }); }, [sup]);

  const create = async () => {
    if (!f.email.trim()) { toast.error("Enter an email"); return; }
    setBusy(true);
    try {
      const body = { email: f.email.trim(), role: f.role };
      if (sup && f.role !== "super_admin") body.org_id = f.org_id;
      const { data } = await http.post("/admin/invites", body);
      const url = window.location.origin + data.invite_path;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success(data.emailed ? `Invite emailed to ${data.email}` : "Invite created — link copied", { description: url });
      setF((p) => ({ ...p, email: "" }));
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const revoke = async (i) => {
    try { await http.delete(`/admin/invites/${i.id}`); toast.success("Invite revoked"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const copy = (i) => { const url = window.location.origin + i.invite_path; navigator.clipboard.writeText(url); toast.success("Link copied", { description: url }); };

  return (
    <Card className="overflow-hidden" data-testid="invites-tab">
      <div className="flex flex-wrap items-end gap-3 p-3 border-b border-[#ECE6DD]">
        <div className="flex-1 min-w-[180px]"><label className={lbl}>Email</label>
          <input data-testid="invite-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="person@company.com" className={inp} /></div>
        <div><label className={lbl}>Role</label>
          <select data-testid="invite-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={inp}>
            {Object.keys(ROLE_LABEL).filter((r) => sup || r !== "super_admin").map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </select></div>
        {sup && f.role !== "super_admin" && (
          <div><label className={lbl}>Organisation</label>
            <select data-testid="invite-org" value={f.org_id} onChange={(e) => setF({ ...f, org_id: e.target.value })} className={inp}>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select></div>
        )}
        <button onClick={create} disabled={busy} data-testid="create-invite-btn" className="flex items-center gap-1 bg-[#A5300F] text-white font-semibold text-sm px-4 py-2 rounded-md hover:bg-[#8F2A0D] disabled:opacity-50"><Plus className="w-4 h-4" /> Invite</button>
      </div>
      <table className="w-full text-sm" data-testid="invites-table">
        <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left bg-[#FFF5EB] border-b border-[#ECE6DD]">
          <th className="py-2 px-4">Email</th><th>Role</th><th>Invited by</th><th>Expires</th><th></th>
        </tr></thead>
        <tbody>
          {invites.map((i) => (
            <tr key={i.id} className="border-b border-[#F4EFE8] hover:bg-[#FBF7F1]" data-testid={`invite-row-${i.id}`}>
              <td className="py-2.5 px-4 font-semibold">{i.email}</td>
              <td><span className="text-xs">{ROLE_LABEL[i.role] || i.role}</span></td>
              <td className="text-xs text-[#8A8175]">{i.invited_by}</td>
              <td className="text-xs text-[#8A8175]">{i.expires_at ? new Date(i.expires_at).toLocaleDateString("en-GB") : "—"}</td>
              <td className="text-right pr-4 whitespace-nowrap">
                <button onClick={() => copy(i)} data-testid={`copy-invite-${i.id}`} className="text-[#A5300F] font-semibold text-xs underline mr-3">Copy link</button>
                <button onClick={() => revoke(i)} data-testid={`revoke-invite-${i.id}`} className="text-[#A5300F] hover:opacity-70 align-middle"><Trash2 className="w-4 h-4 inline" /></button>
              </td>
            </tr>
          ))}
          {invites.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-[#B5AC9E]">No pending invitations. Invite a teammate or client to give them a set-your-own-password link.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function AuditTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { http.get("/admin/audit").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (rows === null) return <div className="py-10 flex justify-center"><Spinner /></div>;
  return (
    <Card className="overflow-hidden" data-testid="audit-tab">
      <table className="w-full text-sm" data-testid="audit-table">
        <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left bg-[#FFF5EB] border-b border-[#ECE6DD]">
          <th className="py-2 px-4">When</th><th>Actor</th><th>Action</th><th>Target</th>
        </tr></thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} className="border-b border-[#F4EFE8] hover:bg-[#FBF7F1]" data-testid={`audit-row-${a.id}`}>
              <td className="py-2.5 px-4 text-xs text-[#8A8175] whitespace-nowrap">{a.at ? new Date(a.at).toLocaleString("en-GB") : "—"}</td>
              <td className="text-xs">{a.actor_email}</td>
              <td><span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ color: "#A5300F", background: "#A5300F14" }}>{a.action}</span></td>
              <td className="text-xs text-[#5C554C]">{a.target_label || a.target_id || "—"}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={4} className="py-10 text-center text-[#B5AC9E]">No audit entries yet. Administrative changes (users, roles, orgs, invites, resets) will appear here.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}
